import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { DateTimeFormatStyle } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import dayjs from "dayjs";
import styles from "./RecentSearchesCard.module.scss";
import { Typography, Card, ActionButton } from "@/components";
import type { RecentSearch } from "@/utils/useRecentSearches";

export const RecentSearchesCard = ({
  search,
  onSelectSearch,
}: {
  search: RecentSearch;
  onSelectSearch: (search: RecentSearch) => void;
}) => {
  const { t, formatDateTime } = useI18nContext();
  const [className, cn] = useModuleBEM(styles, "RecentSearchesCard");

  const formatReservationDate = (dateString: string) => {
    const date = dayjs(dateString).toDate();
    return formatDateTime(date, DateTimeFormatStyle.MediumDate);
  };

  const handleSelectSearch = () => {
    onSelectSearch(search);
  };

  // Type-specific rendering
  const icon =
    search.type === "hotel"
      ? IconNameEnum.hotelReservation
      : IconNameEnum.carReservation;

  const title =
    search.type === "hotel"
      ? t("home.recentSearches.hotelSearches", {
          location: search.label.split(",")[0],
        })
      : t("home.recentSearches.carSearches", {
          location: search.pickupLocation.label.split(",")[0],
        });

  const details =
    search.type === "hotel" ? (
      <>
        <Typography size="sm">
          {formatReservationDate(search.fromDate)}
        </Typography>
        <Icon
          iconName={IconNameEnum.calendarSeparatorIcon}
          useCurrentColor={true}
          className={cn("arrow")}
        />
        <Typography size="sm">
          {formatReservationDate(search.untilDate)}
        </Typography>
        <Typography size="sm" color="var(--text-subdued)">
          {t("home.recentSearches.guestCount", {
            count: search.guests.adults + (search.guests.children?.length ?? 0),
          })}
        </Typography>
      </>
    ) : (
      <>
        <Typography size="sm">
          {formatReservationDate(search.pickupDate)}
        </Typography>
        <Icon
          iconName={IconNameEnum.calendarSeparatorIcon}
          useCurrentColor={true}
          className={cn("arrow")}
        />
        <Typography size="sm">
          {formatReservationDate(search.dropoffDate)}
        </Typography>
      </>
    );

  return (
    <Card className={className}>
      <Icon iconName={icon} useCurrentColor={true} className={cn("icon")} />
      <div className={cn("content")}>
        <Typography weight="bold" size="md">
          {title}
        </Typography>
        <div className={cn("info-dates")}>{details}</div>
      </div>
      <ActionButton
        onClick={handleSelectSearch}
        defaultStyle="outlined"
        message={t("home.recentSearches.cta")}
      />
    </Card>
  );
};
